/**
 * FmChartBlock's pure model: which display mode a block takes for the points
 * it was given, its legend, and the plot figure (legend + leaf, or the sparse
 * data fallback).
 */
import React from 'react';
import type { FmDataDisplayRule } from '../../../types/fm-data-value';
import type { FmLegendEntry } from '../../../types/fm-legend';
import { FmDataBlock, type FmDataBlockItem } from '../FmDataBlock';
import { FmLegend } from '../FmLegend';

export interface FmChartBlockPoint extends FmDataBlockItem {
  color?: string;
}

export type FmChartBlockDisplayMode = 'unavailable' | 'chart' | 'stat' | 'category-stat';

export const DEFAULT_MIN_POINTS_FOR_CHART = 5;
const MIN_ALLOWED_POINTS_FOR_CHART = 5;
const DEFAULT_MAX_BAR_WIDTH = 64;
const DEFAULT_MAX_PLOT_WIDTH = 720;
const MAX_ALLOWED_BAR_WIDTH = 72;
const MAX_ALLOWED_PLOT_WIDTH = 1040;

function clampPositive(value: number | undefined, fallback: number, ceiling: number): number {
  if (value === undefined || !Number.isFinite(value) || value <= 0) return fallback;
  return Math.min(Math.floor(value), ceiling);
}

function isFinitePoint(point: FmChartBlockPoint): boolean {
  if (point.value === null || point.value === undefined) return false;
  if (typeof point.value === 'string' && point.value.trim() === '') return false;
  const numericValue = typeof point.value === 'string' ? Number(point.value) : point.value;
  return Number.isFinite(numericValue);
}

export function resolveLegend(
  data: FmChartBlockPoint[],
  legend: FmLegendEntry[] | undefined,
): FmLegendEntry[] {
  if (legend) return legend;
  return data
    .filter((point): point is FmChartBlockPoint & { color: string } => Boolean(point.color))
    .map((point) => ({
      color: point.color,
      label: point.label,
      value: point.value ?? undefined,
      testId: point.testId ? `${point.testId}-legend` : undefined,
    }));
}

function resolveFmChartBlockDisplayMode(
  pointCount: number,
  shouldRenderChart: boolean,
): FmChartBlockDisplayMode {
  if (pointCount === 0) return 'unavailable';
  if (shouldRenderChart) return 'chart';
  if (pointCount === 1) return 'stat';
  return 'category-stat';
}

type ChartFunctionProps = {
  data: FmChartBlockPoint[];
  availableData: FmChartBlockPoint[];
  pointCount: number;
  unit: string;
  maxBarWidth: number;
  maxPlotWidth: number;
};

export function computeChartBlockState(options: {
  data: FmChartBlockPoint[];
  minPointsForChart: number | undefined;
  sparseMode: 'data' | 'chart' | undefined;
  chart: React.ReactNode | ((props: ChartFunctionProps) => React.ReactNode);
  maxBarWidth: number | undefined;
  maxPlotWidth: number | undefined;
}): {
  availableData: FmChartBlockPoint[];
  pointCount: number;
  safeMaxBarWidth: number;
  safeMaxPlotWidth: number;
  shouldRenderChart: boolean;
  displayMode: FmChartBlockDisplayMode;
  sparseItems: FmChartBlockPoint[];
} {
  const { data, minPointsForChart, sparseMode, chart, maxBarWidth, maxPlotWidth } = options;
  const availableData = data.filter(isFinitePoint);
  const pointCount = availableData.length;
  const safeMaxBarWidth = clampPositive(maxBarWidth, DEFAULT_MAX_BAR_WIDTH, MAX_ALLOWED_BAR_WIDTH);
  const safeMaxPlotWidth = clampPositive(
    maxPlotWidth,
    DEFAULT_MAX_PLOT_WIDTH,
    MAX_ALLOWED_PLOT_WIDTH,
  );
  const safeMinPointsForChart =
    Number.isFinite(minPointsForChart) && minPointsForChart && minPointsForChart > 0
      ? Math.max(MIN_ALLOWED_POINTS_FOR_CHART, Math.floor(minPointsForChart))
      : DEFAULT_MIN_POINTS_FOR_CHART;
  const shouldRenderChart =
    Boolean(chart) &&
    (pointCount >= safeMinPointsForChart || (sparseMode === 'chart' && pointCount > 0));
  const displayMode = resolveFmChartBlockDisplayMode(pointCount, shouldRenderChart);
  const sparseItems = pointCount === 1 ? availableData : data;

  return {
    availableData,
    pointCount,
    safeMaxBarWidth,
    safeMaxPlotWidth,
    shouldRenderChart,
    displayMode,
    sparseItems,
  };
}

export function computeChartBlockFigure(options: {
  pointCount: number;
  shouldRenderChart: boolean;
  chart: React.ReactNode | ((props: ChartFunctionProps) => React.ReactNode);
  data: FmChartBlockPoint[];
  availableData: FmChartBlockPoint[];
  unit: string;
  safeMaxBarWidth: number;
  safeMaxPlotWidth: number;
  sparseItems: FmChartBlockPoint[];
  caption: React.ReactNode;
  valueKind: FmDataDisplayRule['kind'] | undefined;
  locale: string;
  unavailableLabel?: string;
  legend: FmLegendEntry[];
  accessibleName: string;
  takeawayId?: string;
}): React.ReactNode {
  if (options.pointCount === 0) {
    return undefined;
  }
  if (options.shouldRenderChart) {
    return (
      <figure
        className="fm-chart-block__plot"
        aria-label={options.accessibleName}
        aria-describedby={options.takeawayId}
        data-testid="fm-chart-block-plot"
        data-max-bar-width={options.safeMaxBarWidth}
        data-max-plot-width={options.safeMaxPlotWidth}
        style={
          {
            '--chart-block-max-bar-w': `${options.safeMaxBarWidth}px`,
            '--chart-block-max-plot-w': `${options.safeMaxPlotWidth}px`,
          } as React.CSSProperties
        }
      >
        {options.legend.length > 0 ? (
          <FmLegend
            entries={options.legend}
            direction="wrap"
            className="fm-chart-block__legend"
            data-testid="fm-chart-block-legend"
          />
        ) : null}
        {typeof options.chart === 'function'
          ? options.chart({
              data: options.data,
              availableData: options.availableData,
              pointCount: options.pointCount,
              unit: options.unit,
              maxBarWidth: options.safeMaxBarWidth,
              maxPlotWidth: options.safeMaxPlotWidth,
            })
          : options.chart}
      </figure>
    );
  }
  return (
    <FmDataBlock
      items={options.sparseItems}
      unit={options.unit}
      caption={options.caption}
      valueKind={options.valueKind}
      locale={options.locale}
      unavailableLabel={options.unavailableLabel}
      showContext={false}
      variant={options.pointCount === 1 ? 'callout' : 'stat-row'}
      data-testid="fm-chart-block-sparse-fallback"
    />
  );
}
